import { useCallback, useEffect, useRef, useState } from 'react';

interface UseEdgeResizeOptions {
  /** 'x' for a side panel resized by a vertical edge, 'y' for a panel resized by a horizontal edge. */
  axis: 'x' | 'y';
  min: number;
  /** Number, or a getter evaluated on each move so window-relative ceilings track resizes. */
  max: number | (() => number);
  getStartSize: () => number;
  onResize: (next: number) => void;
  onResizeEnd?: () => void;
  collapsePastMin?: number;
  onCollapse?: () => void;
}

const CURSOR_BY_AXIS = { x: 'ew-resize', y: 'ns-resize' } as const;

// While dragging, block pointer events on the rest of the page so an iframe/PDF preview or
// xterm canvas can't capture the mouse and swallow the mouseup, which would make the drag
// "stick" to the cursor after release.
const setPagePointerEvents = (value: 'none' | 'auto') => {
  for (const child of Array.from(document.body.children)) {
    (child as HTMLElement).style.pointerEvents = value;
  }
};

/**
 * Drag-to-resize for a panel docked against the leading edge (right-docked side panel or
 * bottom-docked panel): dragging the leading edge inward grows the panel. Shared by
 * FilePreviewSidebar and SandboxTerminalPanel so the listener wiring + cleanup lives once.
 */
export function useEdgeResize({
  axis,
  min,
  max,
  getStartSize,
  onResize,
  onResizeEnd,
  collapsePastMin,
  onCollapse,
}: UseEdgeResizeOptions): {
  isDragging: boolean;
  onResizeStart: (e: React.MouseEvent) => void;
} {
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef({ start: 0, startSize: 0 });

  // Callers pass fresh closures every render (`onResize`, `max`, `getStartSize`). Read them through a
  // ref so the document listeners can stay stable: depending on them directly made the move/end
  // handlers (and the cleanup effect below) change identity every render, so the first re-render
  // after `onResizeStart` tore down the listeners it had just attached and froze the drag.
  const opts = {
    axis,
    min,
    max,
    getStartSize,
    onResize,
    onResizeEnd,
    collapsePastMin,
    onCollapse,
  };
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const endRef = useRef<() => void>(() => {});

  const handleResizeMove = useCallback((e: MouseEvent) => {
    const { axis, min, max, onResize, collapsePastMin, onCollapse } =
      optsRef.current;
    const position = axis === 'x' ? e.clientX : e.clientY;
    const delta = position - dragState.current.start;
    // Leading edge: dragging it inward (negative delta) grows the panel.
    const raw = dragState.current.startSize - delta;
    if (
      onCollapse &&
      collapsePastMin !== undefined &&
      raw < min - collapsePastMin
    ) {
      endRef.current();
      onCollapse();
      return;
    }
    const ceiling = typeof max === 'function' ? max() : max;
    onResize(Math.min(ceiling, Math.max(min, raw)));
  }, []);

  const handleResizeEnd = useCallback(() => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = 'auto';
    document.body.style.userSelect = '';
    setPagePointerEvents('auto');
    optsRef.current.onResizeEnd?.();
  }, [handleResizeMove]);
  endRef.current = handleResizeEnd;

  const onResizeStart = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return; // primary button only
      e.preventDefault();
      const { axis, getStartSize } = optsRef.current;
      dragState.current = {
        start: axis === 'x' ? e.clientX : e.clientY,
        startSize: getStartSize(),
      };
      setIsDragging(true);
      document.body.style.cursor = CURSOR_BY_AXIS[axis];
      document.body.style.userSelect = 'none';
      setPagePointerEvents('none');
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    },
    [handleResizeMove, handleResizeEnd]
  );

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'auto';
      document.body.style.userSelect = '';
      setPagePointerEvents('auto');
    };
  }, [handleResizeMove, handleResizeEnd]);

  return { isDragging, onResizeStart };
}
